import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Languages from '../Languages/Languages';
import { ContainedButton } from '../../pages/hygienist/AvailableJobPage/AvailableJob.styles';
import useActionApi from '../../hooks/useActionApi';
import useApi from '../../hooks/useApi';
import Api from '../../api';
import FixedLoader from '../FixedLoader/FixedLoader';
import {
  StyledDialog,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ButtonContainer,
  SuccessMessage
} from './LanguageSelectionModal.styles';

interface LanguageSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // Remove hygienistCountry prop - we'll get it from API
}

const LanguageSelectionModal: React.FC<LanguageSelectionModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [selectedLanguages, setSelectedLanguages] = useState<number[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Fetch region-specific languages from API
  const [languageData, languageLoading, languageError, fetchLanguages] = useApi({
    apiMethod: Api.getRegionalLanguages,
    initialValue: { country: 0, common: [], additional: [] }
  });

  // Load languages when modal opens
  useEffect(() => {
    if (open) {
      fetchLanguages({});
    }
  }, [open, fetchLanguages]);

  // Pre-select English when languages are loaded
  useEffect(() => {
    if (open && languageData.common.length > 0) {
      const englishLang = [...languageData.common, ...languageData.additional]
        .find(lang => lang.name.toLowerCase() === 'english');
      
      if (englishLang) {
        setSelectedLanguages([englishLang.id]);
      } else {
        setSelectedLanguages([]);
      }
      setShowSuccess(false);
    }
  }, [open, languageData]);

  const [submitLanguagesLoading, , submitLanguages] = useActionApi({
    apiMethod: async () => {
      // Remove all existing languages first (clean slate)
      const currentLanguages = await Api.getMyLanguages();
      if (currentLanguages.length > 0) {
        await Api.removeLanguages(currentLanguages.map(l => l.id));
      }
      
      // Add newly selected languages
      await Api.addLanguages(selectedLanguages);
      
      return { success: true };
    },
    onSuccess: () => {
      setShowSuccess(true);
      // Show success message briefly, then call onSuccess
      setTimeout(() => {
        setShowSuccess(false);
        onSuccess();
      }, 1500);
    },
    onError: (error) => {
      console.error('Failed to submit languages:', error);
      // TODO: Show error message to user
    }
  });

  const handleLanguageChange = useCallback((languageIds: number[]) => {
    setSelectedLanguages(languageIds);
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedLanguages.length === 0) {
      // At least English should be selected, but add validation if needed
      return;
    }
    submitLanguages({});
  }, [selectedLanguages, submitLanguages]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={true}
      PaperProps={{
        style: { zIndex: 10000 }
      }}
      sx={{ zIndex: 10000 }}
    >
      <DialogTitle>
        <ModalTitle>Add Languages to Your Profile</ModalTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          disabled={submitLanguagesLoading || languageLoading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <ModalContent>
          {languageLoading ? (
            <FixedLoader loading={true} />
          ) : showSuccess ? (
            <SuccessMessage>
              ✅ Languages successfully added to your profile!
            </SuccessMessage>
          ) : (
            <>
              <ModalDescription>
                Dental offices love to know which languages you speak – it often helps 
                them choose the best match for their patients.
              </ModalDescription>
              
              <ModalDescription>
                Please take a moment to add the languages you're comfortable with.
              </ModalDescription>

              <Languages
                commonLanguages={languageData.common}
                otherLanguages={languageData.additional}
                initialSelected={[...languageData.common, ...languageData.additional].filter(l => selectedLanguages.includes(l.id))}
                onChange={handleLanguageChange}
              />

              <ButtonContainer>
                <ContainedButton
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={submitLanguagesLoading || selectedLanguages.length === 0}
                  fullWidth
                >
                  {submitLanguagesLoading ? 'Submitting...' : 'Submit Languages'}
                </ContainedButton>
              </ButtonContainer>
            </>
          )}
        </ModalContent>
      </DialogContent>

      <FixedLoader loading={submitLanguagesLoading} />
    </StyledDialog>
  );
};

export default LanguageSelectionModal; 